import { Col, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import sidebar from "../../utils/sidebar";
import FixedSave from "../../../../components/Actions/FixedSave";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { get, post } from "../../../../utils/helpers/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { useSettings } from "../../../../context/settings";

function CreateTaskPage() {
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [initLoading, setInitLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [customersData, setCustomersData] = useState([]);
  const [employeesData, setEmployeesData] = useState([]);
  const [leadsData, setLeadsData] = useState([]);
  const [taskData, setTaskData] = useState({
    customer_id: state?.taskData?.customer_id || "",
    lead_id: state?.taskData?.lead_id || null,
    task_for: state?.taskData?.task_for || "customer",
    assigned_to: state?.taskData?.assigned_to || "",
    subject: state?.taskData?.subject || "",
    due_date: state?.taskData?.due_date ? parseISO(state.taskData.due_date) : null,
    status: state?.taskData?.status || 'not_started',
    priority: state?.taskData?.priority || 'normal',
    remind_before: state?.taskData?.remind_before || 10,
    description: state?.taskData?.description || ''
  });

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    setInitLoading(true);
    const data = await get('/crm/tasks/create');
    if (data.success) {
      setCustomersData(data.customers);
      setEmployeesData(data.employees);
      setLeadsData(data.leads);
      setInitLoading(false);
    }
  };

  const saveTask = async () => {
    setSaveLoading(true);
    const url = id ? '/crm/tasks/update/' + id : '/crm/tasks/store';
    const data = await post(url, {
      ...taskData,
      due_date: taskData.due_date ? format(taskData.due_date, 'yyyy-MM-dd') : null,
    });
    if (data.success) {
      navigate('/crm/tasks');
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('crm.tasks'), id ? t('crm.editTask') : t('crm.newTask')]}
          onBack={() => navigate('/crm/tasks')}
        />

        <div className="subheader-page-content scrollable">
          <Row>
            <Col xs="6" className="mb-3">
              <Label>{t('crm.taskFor')} <span className="text-danger">*</span></Label>
              <Row className="g-1">
                <Col xs="4">
                  <Input type="select" value={taskData.task_for} onChange={e => setTaskData({ ...taskData, task_for: e.target.value, customer_id: null, lead_id: null })}>
                    <option value="customer">{t('crm.customer')}</option>
                    <option value="lead">{t('crm.lead')}</option>
                  </Input>
                </Col>
                <Col xs="8">
                  {taskData.task_for === 'customer' ? (
                    <Select
                      options={customersData.map(c => ({ value: c.id, label: c.firstname }))}
                      styles={SelectCustomStyles}
                      value={customersData.map(c => ({ value: c.id, label: c.firstname })).find(o => o.value === taskData.customer_id) || null}
                      onChange={selected => setTaskData({ ...taskData, customer_id: selected ? selected.value : null })}
                    />
                  ) : (
                    <Select
                      options={leadsData.map(l => ({ value: l.id, label: l.firstname + ' ' + l.lastname }))}
                      styles={SelectCustomStyles}
                      value={leadsData.map(l => ({ value: l.id, label: l.firstname + ' ' + l.lastname })).find(o => o.value === taskData.lead_id) || null}
                      onChange={selected => setTaskData({ ...taskData, lead_id: selected ? selected.value : null })}
                    />
                  )}
                </Col>
              </Row>
            </Col>
            <Col xs="6" className="mb-3">
              <Label>{t('crm.relatedTo')} </Label>
              <Select
                options={employeesData.map(e => ({ value: e.id, label: e.firstname + ' ' + e.lastname }))}
                styles={SelectCustomStyles}
                value={employeesData.map(e => ({ value: e.id, label: e.firstname + ' ' + e.lastname })).find(o => o.value === taskData.assigned_to) || null}
                onChange={selected => setTaskData({ ...taskData, assigned_to: selected ? selected.value : null })}
              />
            </Col>
            <Col xs="6" className="mb-3">
              <Label>{t('common.subject')} <span className="text-danger">*</span></Label>
              <Input type="text" value={taskData.subject} onChange={e => setTaskData({ ...taskData, subject: e.target.value })} />
            </Col>
            <Col xs="6" className="mb-3">
              <Label>{t('sales.dueDate')} </Label>
              <DatePicker
                selected={taskData.due_date}
                onChange={(d) => setTaskData({ ...taskData, due_date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>
            <Col xs="6" className="mb-3">
              <Label>{t('common.status')}</Label>
              <Input type="select" value={taskData.status} onChange={e => setTaskData({ ...taskData, status: e.target.value })}>
                <option value="not_started">{t('common.notStarted')}</option>
                <option value="deferred">{t('common.deferred')}</option>
                <option value="in_progress">{t('common.inProgress')}</option>
                <option value="completed">{t('common.completed')}</option>
                <option value="canceled">{t('common.canceled')}</option>
              </Input>
            </Col>
            <Col xs="6" className="mb-3">
              <Label>{t('crm.priority')} </Label>
              <Input type="select" value={taskData.priority} onChange={e => setTaskData({ ...taskData, priority: e.target.value })}>
                <option value="low">{t('common.low')}</option>
                <option value="normal">{t('common.normal')}</option>
                <option value="high">{t('common.high')}</option>
              </Input>
            </Col>
            <Col md="6" className="mb-3">
              <Label>{t('crm.remindBefore')} </Label>
              <Input type="select" value={taskData.remind_before} onChange={e => setTaskData({ ...taskData, remind_before: e.target.value })}>
                <option value="10">{t('crm.remind10Min')}</option>
                <option value="30">{t('crm.remind30Min')}</option>
                <option value="60">{t('crm.remind1Hour')}</option>
                <option value="120">{t('crm.remind2Hours')}</option>
                <option value="1440">{t('crm.remind1Day')}</option>
                <option value="2880">{t('crm.remind2Days')}</option>
              </Input>
            </Col>
            <Col md="12" className="mb-3">
              <Label>{t('common.description')}</Label>
              <Input type="textarea" rows="4" value={taskData.description} onChange={e => setTaskData({ ...taskData, description: e.target.value })} />
            </Col>
          </Row>
        </div>
      </div>

      <FixedSave 
        onSave={saveTask} 
        loading={saveLoading} 
        disabled={initLoading || saveLoading || !taskData.subject || (!taskData.customer_id && !taskData.lead_id)} 
      />
    </SidebarLayout>
  );
}

export default CreateTaskPage;
